// App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { authInterceptor } from './utils/AuthInterceptor';
import './utils/AuthInterceptorTest'; // Import test to run automatically
import Header from './Component/Header';
import AppRoutes from './Component/Route';

// Conditional import for devtools (only in development)
let ReactQueryDevtools = null;
if (process.env.NODE_ENV === 'development') {
  try {
    const devtools = require('@tanstack/react-query-devtools');
    ReactQueryDevtools = devtools.ReactQueryDevtools;
  } catch (e) {
    console.warn('React Query Devtools not available');
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: (failureCount, error) => {
        if (error?.message === 'Session expired' || error?.status === 401 || error?.status === 403) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
  },
});

// Initialize interceptor immediately when module loads
console.log('🚀 App module loading - initializing auth interceptor early');
authInterceptor.initEarly();

// Inner component to access navigate hook
const AppContent = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Update auth interceptor with proper navigation
    console.log('🔗 Connecting navigate function to auth interceptor');
    authInterceptor.init(navigate);
  }, [navigate]);

  return <AppRoutes />;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppContent />
      </Router>
      
      {/* Conditionally render devtools */}
      {ReactQueryDevtools && process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      )}
    </QueryClientProvider>
  );
};

export default App;
